import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, Star, Map, LayoutGrid, Tent, Caravan, Truck, Droplets, Zap, Wifi, SlidersHorizontal, X, Award } from "lucide-react";
import api from "@/lib/api";
import MapComponent from "@/components/MapComponent";
import { useSiteSettings } from "@/contexts/SettingsContext";
import FavoriteButton from "@/components/FavoriteButton";

const DEFAULT_IMAGES = [
  "/images/hero.jpg",
];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TERRAIN_LABELS = { rv: "VR", tent: "Tente", van: "Van", trailer: "Roulotte", other: "Autre", all: "Tous" };
const TERRAIN_ICONS = { rv: Caravan, tent: Tent, van: Truck, trailer: Caravan, other: Tent };
const AMENITY_FILTERS = [
  { key: "water", label: "Eau" }, { key: "electricity_15a", label: "Elec 15A" },
  { key: "electricity_30a", label: "Elec 30A" }, { key: "electricity_50a", label: "Elec 50A" },
  { key: "ev_charger", label: "Borne VE" }, { key: "sewer", label: "Egout" },
  { key: "toilet", label: "Toilette" }, { key: "shower", label: "Douche" },
  { key: "wifi", label: "WiFi" }, { key: "fire_pit", label: "Feu" },
];

function getImageUrl(s, i) {
  const img = s.photos?.[0] || s.image_url;
  if (!img) return DEFAULT_IMAGES[i % DEFAULT_IMAGES.length];
  if (img.startsWith("/api/")) return `${BACKEND_URL}${img}`;
  return img;
}

export default function SpacesListPage() {
  const [searchParams] = useSearchParams();
  const { settings } = useSiteSettings();
  const listTitle = settings?.list_title || "Terrains disponibles";
  const listEmptyMsg = settings?.list_empty_msg || "Aucun terrain trouve";
  const [spaces, setSpaces] = useState([]);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [terrainType, setTerrainType] = useState("");
  const [region, setRegion] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [selectedAmenities, setSelectedAmenities] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);

  const toggleAmenity = (key) => setSelectedAmenities(a => ({ ...a, [key]: !a[key] }));

  const fetchSpaces = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (terrainType && terrainType !== "all") params.set("terrain_type", terrainType);
      if (region) params.set("region", region);
      if (minPrice) params.set("min_price", minPrice);
      if (maxPrice) params.set("max_price", maxPrice);
      const amenKeys = Object.entries(selectedAmenities).filter(([, v]) => v).map(([k]) => k).join(",");
      if (amenKeys) params.set("amenities_filter", amenKeys);
      const { data } = await api.get(`/spaces?${params.toString()}`);
      setSpaces(data);
    } catch (err) {
      console.error("Failed to load spaces:", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSpaces(); }, []); // eslint-disable-line
  const handleSearch = (e) => { e.preventDefault(); fetchSpaces(); };
  const clearFilters = () => {
    setSearch(""); setTerrainType(""); setRegion(""); setMinPrice(""); setMaxPrice(""); setSelectedAmenities({});
    setTimeout(fetchSpaces, 50);
  };

  const activeFilterCount = [
    terrainType && terrainType !== "all", region, minPrice, maxPrice,
    ...Object.values(selectedAmenities)
  ].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950" data-testid="spaces-list-title">{listTitle}</h1>
        <div className="flex gap-1 bg-zinc-100 rounded-xl p-1">
          <Button variant={viewMode === "grid" ? "default" : "ghost"} size="icon" className="h-8 w-8 rounded-lg" onClick={() => setViewMode("grid")} data-testid="view-grid-btn"><LayoutGrid className="h-4 w-4" /></Button>
          <Button variant={viewMode === "map" ? "default" : "ghost"} size="icon" className="h-8 w-8 rounded-lg" onClick={() => setViewMode("map")} data-testid="view-map-btn"><Map className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Search + Filter Toggle */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1" data-testid="spaces-search-form">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par region, ville..." className="pl-9 bg-white rounded-xl" data-testid="spaces-search-input" /></div>
          <Button type="submit" className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl" data-testid="spaces-search-btn">Rechercher</Button>
        </form>
        <Button variant={showFilters ? "default" : "outline"} className="rounded-xl" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
          <SlidersHorizontal className="h-4 w-4 mr-2" /> Filtres {activeFilterCount > 0 && <Badge className="ml-2 bg-emerald-600 text-white border-0 text-xs">{activeFilterCount}</Badge>}
        </Button>
        {activeFilterCount > 0 && <Button variant="ghost" className="rounded-xl text-red-500" onClick={clearFilters} data-testid="clear-all-filters"><X className="h-4 w-4 mr-1" /> Effacer tout</Button>}
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <Card className="border border-zinc-200 rounded-2xl p-5 mb-6 bg-white animate-fade-in" data-testid="filters-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1.5 block">Type de terrain</label>
              <Select value={terrainType} onValueChange={setTerrainType}>
                <SelectTrigger className="rounded-xl" data-testid="filter-terrain-type"><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="rv">VR / Motorisé</SelectItem>
                  <SelectItem value="tent">Tente</SelectItem>
                  <SelectItem value="van">Van aménagé</SelectItem>
                  <SelectItem value="trailer">Roulotte / Caravane</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1.5 block">Région</label>
              <Input value={region} onChange={e => setRegion(e.target.value)} placeholder="Laurentides, Cantons..." className="rounded-xl" data-testid="filter-region" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1.5 block">Prix min ($/nuit)</label>
              <Input type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)} placeholder="0" className="rounded-xl" data-testid="filter-min-price" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1.5 block">Prix max ($/nuit)</label>
              <Input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="500" className="rounded-xl" data-testid="filter-max-price" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 block">Commodites</label>
            <div className="flex flex-wrap gap-2">
              {AMENITY_FILTERS.map(a => (
                <label key={a.key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-sm ${selectedAmenities[a.key] ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`} data-testid={`filter-amenity-${a.key}`}>
                  <Checkbox checked={!!selectedAmenities[a.key]} onCheckedChange={() => toggleAmenity(a.key)} className="h-3.5 w-3.5" />
                  {a.label}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={fetchSpaces} className="mt-4 bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl" data-testid="apply-filters-btn">Appliquer les filtres</Button>
        </Card>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>
      ) : spaces.length === 0 ? (
        <div className="text-center py-20" data-testid="no-spaces-message"><MapPin className="h-12 w-12 text-zinc-300 mx-auto mb-4" /><p className="text-zinc-500 text-lg">{listEmptyMsg}</p><p className="text-zinc-400 text-sm mt-1">Modifiez vos filtres ou votre recherche</p></div>
      ) : viewMode === "map" ? (
        <div className="rounded-2xl overflow-hidden border border-zinc-200 relative" data-testid="map-view">
          <MapComponent spaces={spaces} height="550px" />
          {spaces.length > 0 && spaces.filter(s => s.lat && s.lng).length < spaces.length && (
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-xl px-3 py-2 text-xs text-zinc-700 shadow-md border border-amber-200 z-[400]" data-testid="map-notice">
              <span className="text-amber-600 font-semibold">{spaces.filter(s => s.lat && s.lng).length}</span> terrain{spaces.filter(s => s.lat && s.lng).length > 1 ? "s" : ""} sur <span className="font-semibold">{spaces.length}</span> sur la carte
              <br /><span className="text-zinc-400 text-[11px]">Les autres n&apos;ont pas d&apos;adresse geolocalisable</span>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {spaces.map((space, i) => {
            const types = (space.terrain_types && space.terrain_types.length > 0) ? space.terrain_types : [space.terrain_type];
            const primary = types[0] || "rv";
            const TIcon = TERRAIN_ICONS[primary] || Tent;
            const amenities = space.amenities || {};
            return (
              <Link to={`/spaces/${space.id}`} key={space.id} data-testid={`space-card-${i}`}>
                <Card className="border border-zinc-200 rounded-2xl bg-white overflow-hidden hover:shadow-md transition-all duration-200 hover:-translate-y-[2px]">
                  <div className="aspect-video overflow-hidden relative">
                    <img src={getImageUrl(space, i)} alt={space.title} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 flex items-center gap-2">
                      <FavoriteButton spaceId={space.id} size="sm" />
                      <Badge className="bg-white/90 text-zinc-950 backdrop-blur-sm border-0">
                        <TIcon className="h-3 w-3 mr-1" />
                        {types.map(t => TERRAIN_LABELS[t]).filter(Boolean).join(" · ") || "VR"}
                      </Badge>
                    </div>
                    {space.host_is_superstar && (
                      <Badge className="absolute top-3 left-3 bg-gradient-to-r from-amber-400 to-amber-500 text-white border-0 gap-1 shadow-md" data-testid={`superstar-card-${i}`}>
                        <Award className="h-3 w-3" /> Superstar
                      </Badge>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1"><h3 className="font-heading font-bold text-zinc-950 truncate">{space.title}</h3>{space.is_free ? <span className="text-amber-600 font-bold text-sm whitespace-nowrap ml-2">Gratuit</span> : <span className="text-emerald-600 font-bold text-sm whitespace-nowrap ml-2">{space.price_per_night?.toFixed(2)} $/nuit</span>}</div>
                    <p className="text-sm text-zinc-500 flex items-center gap-1"><MapPin className="h-3 w-3 flex-shrink-0" /> {space.city}{space.region ? `, ${space.region}` : ""}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {amenities.water && <Droplets className="h-3.5 w-3.5 text-blue-500" />}
                      {(amenities.electricity_15a || amenities.electricity_30a || amenities.electricity_50a) && <Zap className="h-3.5 w-3.5 text-amber-500" />}
                      {amenities.ev_charger && <Zap className="h-3.5 w-3.5 text-emerald-500" />}
                      {amenities.wifi && <Wifi className="h-3.5 w-3.5 text-zinc-500" />}
                      {space.avg_rating > 0 && (<><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400 ml-auto" /><span className="text-xs font-medium text-zinc-700">{space.avg_rating} ({space.review_count})</span></>)}
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
